import {
	Button,
	Checkbox,
	Group,
	Input,
	LoadingOverlay,
	Table,
	Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconList, IconPencilPlus, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { KeyedRow } from '../../components/KeyedRow';
import { PageHeader } from '../../components/PageHeader';
import { ProductFilterButton } from '../../components/ProductFilterButton';
import { Title } from '../../components/Title';
import { FormBranchSelector } from '../../components/form/FormBranchSelector';
import { useModal } from '../../hooks/useModal';
import { usePagination } from '../../hooks/usePagination';
import { useProductFilterForm } from '../../hooks/useProductFilterForm';
import { useRowSelection } from '../../hooks/useRowSelection';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import { BulkUpdateProductModal } from './components/BulkUpdateProductModal';
import { NewProductModal } from './components/NewProductModal';
import { ProductRow } from './components/ProductRow';
import type { ProductDto } from './types/ProductDto';

export function Products() {
	const pagination = usePagination();
	const { organisation } = useParams();
	const { form, urlParams } = useProductFilterForm(pagination);

	const [isLoading, setIsLoading] = useState(false);

	const productsQuery = query<ProductDto[]>(
		`${organisation}/product/all?${urlParams.valueWithPagination}`,
	);

	const productsCountQuery = query<number>(
		`${organisation}/product/count?${urlParams.value}`,
	);

	const defaultPharmacyQuery = query<string>(
		`${organisation}/branches/default-id`,
	);

	useEffect(() => {
		if (defaultPharmacyQuery.data) {
			form.setFieldValue('branchId', defaultPharmacyQuery.data?.value ?? '');
		}
	}, [defaultPharmacyQuery.data]);

	useEffect(() => {
		selection.deselectAll();
	}, [form.values]);

	const [
		isNewProductModalOpen,
		{ open: openNewProductModal, close: closeNewProductModal },
	] = useDisclosure(false);

	const bulkEditModal = useModal();

	const selection = useRowSelection<string>(
		productsCountQuery.data?.value ?? 0,
		(key, rowKey) => key === rowKey,
	);

	return (
		<>
			<BulkUpdateProductModal
				isOpened={bulkEditModal.isOpen}
				close={bulkEditModal.close}
				onSuccess={() => {
					productsQuery.remove();
					productsQuery.refetch();
				}}
				rowSelection={selection}
				getSkuid={(skuid) => skuid}
				urlParams={urlParams.value}
			/>
			<NewProductModal
				isOpened={isNewProductModalOpen}
				close={closeNewProductModal}
				triggerListUpdate={() => {
					productsQuery.remove();
					productsCountQuery.remove();

					productsQuery.refetch();
					productsCountQuery.refetch();
				}}
			/>
			<PageHeader title={'Products'} hideFiltering>
				<form>
					<Group justify={'flex-end'}>
						<Tooltip label={'Search SKUID or description...'}>
							<Input
								variant={'borderless-filled'}
								placeholder={'Search SKUID or description...'}
								leftSection={<IconSearch {...defaultIconStyle} />}
								w={'20vw'}
								key={form.key('searchQuery')}
								{...form.getInputProps('searchQuery')}
							/>
						</Tooltip>
						<FormBranchSelector
							form={form}
							property={'branchId'}
							placeHolder={'Select branch...'}
							forFilter={true}
							clearable={false}
							hideArea={true}
							variant={'borderless-filled'}
						/>
						<ProductFilterButton form={form} />
						<Button
							{...whiteIconButtonStyle}
							onClick={() => openNewProductModal()}
						>
							<IconPencilPlus {...defaultIconStyle} />
						</Button>
						<Tooltip label={'Bulk edit product information'}>
							<Button
								{...whiteIconButtonStyle}
								onClick={bulkEditModal.open}
								disabled={selection.noneSelected}
							>
								<IconList {...defaultIconStyle} />
							</Button>
						</Tooltip>
					</Group>
				</form>
			</PageHeader>
			<LoadingOverlay visible={isLoading} />
			<DataTable
				pagination={pagination}
				totalItems={productsCountQuery.data?.value}
				isRowsLoading={productsQuery.isLoading}
				isPaginationLoading={productsCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Checkbox
								color={'medepos.1'}
								checked={selection.allSelected}
								indeterminate={selection.someSelected}
								onChange={selection.toggleAll}
							/>
						</Table.Th>
						<Table.Th>
							<Title label={'SKUID'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Description'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Inc. VAT Price'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Group'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Department'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Supplier'} />
						</Table.Th>
						<Table.Th>
							<Title label={'VAT Rate'} />
						</Table.Th>
						<Table.Th>
							<Title label={'View'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{productsQuery.data?.value.map((p) => (
						<ProductRow
							originalProduct={p}
							triggerListUpdate={() => {
								productsQuery.remove();
								productsCountQuery.remove();

								productsQuery.refetch();
								productsCountQuery.refetch();
							}}
							setIsLoading={setIsLoading}
							isSelected={selection.isSelected(p.skuid)}
							onToggle={() => selection.toggleRow(p.skuid)}
							key={p.skuid}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
